import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { getProfitReport, getSalesChart } from '../api/sales';
import { getFromCache } from '../services/cacheService';
import { getItemName } from '../utils/itemHelpers';
import { exportToCSV } from '../utils/csvExport';
import { LineChart, Line, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import Navbar from '../components/Navbar';
import '../styles/Reports.css';

const Reports = () => {
  const { t, i18n } = useTranslation();
  const [profitData, setProfitData] = useState(null);
  const [chartData, setChartData] = useState(null);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [paymentFilter, setPaymentFilter] = useState('all');
  const [invoiceFilter, setInvoiceFilter] = useState('all');
  const [loading, setLoading] = useState(false);
  const isRTL = i18n.dir() === 'rtl';

  const toNumber = (value) => {
    const n = Number(value);
    return Number.isFinite(n) ? n : 0;
  };

  const withTimeout = (promise, ms = 12000) => {
    return Promise.race([
      promise,
      new Promise((_, reject) =>
        setTimeout(() => reject(new Error('Reports request timeout')), ms)
      ),
    ]);
  };

  // Colors for charts
  const COLORS = ['#FF6B6B', '#4ECDC4', '#45B7D1', '#FFA07A', '#98D8C8', '#F7DC6F', '#BB8FCE', '#85C1E2', '#F8B88B', '#AED6F1'];
  const CURRENCY_COLORS = { USD: '#27ae60', LBP: '#3498db' };

  const getWholesaleSalesLabel = () => {
    const label = t('wholesaleSales');
    if (label && label !== 'wholesaleSales') return label;
    if (i18n.language === 'ar') return 'فواتير جملة';
    if (i18n.language === 'fr') return 'Factures Gros';
    return 'Wholesale Invoices';
  };

  const getRetailSalesLabel = () => {
    const label = t('retailSales');
    if (label && label !== 'retailSales') return label;
    if (i18n.language === 'ar') return 'فواتير مفرق';
    if (i18n.language === 'fr') return 'Factures Détail';
    return 'Retail Invoices';
  };

  // Memoized calculations for better performance
  const profitByCategoryAndCurrency = useMemo(() => {
    if (!profitData || !profitData.items) return { USD: [], LBP: [] };
    const usdCategories = {};
    const lbpCategories = {};
    
    profitData.items.forEach(item => {
      // Get category name in current language
      let catName = 'Other';
      if (item.category_name) {
        if (i18n.language === 'ar' && item.category_name_ar) {
          catName = item.category_name_ar;
        } else if (i18n.language === 'fr' && item.category_name_fr) {
          catName = item.category_name_fr;
        } else {
          catName = item.category_name;
        }
      }
      
      // Include profit and loss (non-zero values) so category totals are accurate.
      if (item.profit_usd !== 0) {
        if (!usdCategories[catName]) {
          usdCategories[catName] = { name: catName, profit: 0, count: 0, currency: 'USD' };
        }
        usdCategories[catName].profit += item.profit_usd;
        usdCategories[catName].count += item.total_sold_usd || 0;
      }
      
      if (item.profit_lbp !== 0) {
        if (!lbpCategories[catName]) {
          lbpCategories[catName] = { name: catName, profit: 0, count: 0, currency: 'LBP' };
        }
        lbpCategories[catName].profit += item.profit_lbp;
        lbpCategories[catName].count += item.total_sold_lbp || 0;
      }
    });
    
    return {
      USD: Object.values(usdCategories),
      LBP: Object.values(lbpCategories)
    };
  }, [profitData, i18n.language]);

  const hasMixedCurrencies = useMemo(() => {
    if (!profitData || !profitData.items) return false;
    const hasUSD = profitData.items.some(item => item.currency === 'USD');
    const hasLBP = profitData.items.some(item => item.currency === 'LBP');
    return hasUSD && hasLBP;
  }, [profitData]);

  const salesByCurrency = useMemo(() => {
    if (!profitData || !profitData.items) return [];
    const currencies = {};
    profitData.items.forEach(item => {
      const curr = item.currency || 'USD';
      if (!currencies[curr]) {
        currencies[curr] = { name: curr, profit: 0, sales: 0 };
      }
      currencies[curr].profit += curr === 'USD' ? item.profit_usd : item.profit_lbp;
      currencies[curr].sales += item.total_sold;
    });
    return Object.values(currencies);
  }, [profitData]);

  const salesPercentageByItem = useMemo(() => {
    if (!profitData || !profitData.items) return [];
    
    const total = profitData.items.reduce((sum, item) => sum + (item.total_sold || 0), 0);
    
    return profitData.items.map(item => ({
      name: getItemName(item, i18n.language),
      value: item.total_sold || 0,
      percentage: total > 0 ? (((item.total_sold || 0) / total) * 100).toFixed(1) : 0,
      total_sold_usd: item.total_sold_usd || 0,
      total_sold_lbp: item.total_sold_lbp || 0,
      total_value_usd: item.total_value_usd || 0,
      total_value_lbp: item.total_value_lbp || 0,
      profit_usd: item.profit_usd || 0,
      profit_lbp: item.profit_lbp || 0
    }));
  }, [profitData, i18n.language]);

  const salesDistributionChartMargin = useMemo(() => {
    if (isRTL) {
      return { top: 5, right: 180, left: 20, bottom: 5 };
    }
    return { top: 5, right: 20, left: 180, bottom: 5 };
  }, [isRTL]);

  const fetchReports = async (start, end, selectedPaymentFilter = 'all', selectedInvoiceFilter = 'all') => {
    setLoading(true);
    try {
      const [profit, sales] = await withTimeout(
        Promise.all([
          getProfitReport(start, end, 'all', selectedPaymentFilter, selectedInvoiceFilter),
          getSalesChart(start, end, 'all', selectedPaymentFilter, selectedInvoiceFilter)
        ])
      );

      // Ensure data is properly formatted
      setProfitData(profit || { total_profit_usd: 0, total_profit_lbp: 0, items: [] });
      setChartData(sales || { data: [] });
    } catch (error) {
      console.error('Error fetching reports:', error);
      
      // Try to use cached receipts when offline
      if (!navigator.onLine) {
        const cachedReceipts = await getFromCache('RECEIPTS');
        
        if (cachedReceipts) {
          console.log('[Reports] Using cached receipts for offline report');
          // Filter receipts by date range
          const filteredReceipts = (Array.isArray(cachedReceipts) ? cachedReceipts : cachedReceipts.results || [])
            .filter(receipt => {
              const receiptDate = new Date(receipt.sale_date).toISOString().split('T')[0];
              const dateMatch = receiptDate >= start && receiptDate <= end;
              if (!dateMatch) return false;

              if (selectedInvoiceFilter === 'wholesale' && !receipt.is_wholesale) return false;
              if (selectedInvoiceFilter === 'retail' && receipt.is_wholesale) return false;

              const method = (receipt.payment_method || '').toLowerCase();
              if (selectedPaymentFilter !== 'all' && method !== selectedPaymentFilter) return false;

              return true;
            });
          
          // Create simplified profit data from cached receipts
          if (filteredReceipts.length > 0) {
            const items = [];
            let totalUSD = 0;
            let totalLBP = 0;
            
            filteredReceipts.forEach(receipt => {
              receipt.items?.forEach(item => {
                const profitUSD = item.subtotal_usd || 0;
                const profitLBP = item.subtotal_lbp || 0;
                totalUSD += profitUSD;
                totalLBP += profitLBP;
                items.push({
                  ...item,
                  total_sold: item.quantity,
                  currency: profitUSD > 0 ? 'USD' : 'LBP',
                  profit_usd: profitUSD,
                  profit_lbp: profitLBP
                });
              });
            });
            
            setProfitData({ 
              total_profit_usd: totalUSD, 
              total_profit_lbp: totalLBP, 
              items 
            });
            setChartData({ data: [] });
            setLoading(false);
            return;
          }
        }
      }
      
      toast.error(t('errorFetchingReports'));
      // Set empty data on error
      setProfitData({ total_profit_usd: 0, total_profit_lbp: 0, items: [] });
      setChartData({ data: [] });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Initialize default period once (current month).
    const now = new Date();
    const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
    const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    
    const start = firstDay.toISOString().split('T')[0];
    const end = lastDay.toISOString().split('T')[0];
    
    setStartDate(start);
    setEndDate(end);
    
  }, []);

  useEffect(() => {
    // Refetch when date/payment/invoice filters change
    if (startDate && endDate) {
      if (new Date(startDate) <= new Date(endDate)) {
        fetchReports(startDate, endDate, paymentFilter, invoiceFilter);
      }
    }
  }, [startDate, endDate, paymentFilter, invoiceFilter]);

  const handleDateChange = () => {
    if (startDate && endDate) {
      if (new Date(startDate) > new Date(endDate)) {
        toast.error(t('invalidDateRange'));
        return;
      }
      fetchReports(startDate, endDate, paymentFilter, invoiceFilter);
    }
  };

  const handleExportCSV = () => {
    if (!profitData || !profitData.items) {
      toast.error(t('noDataForPeriod') || 'No data to export');
      return;
    }

    const headers = [t('item'), t('category'), t('currency'), t('totalSold'), t('profitUSD'), t('profitLBP')];
    const rows = profitData.items.map(item => [
      getItemName(item, i18n.language),
      item.category_name || '',
      item.currency,
      toNumber(item.total_sold),
      toNumber(item.profit_usd).toFixed(2),
      toNumber(item.profit_lbp).toFixed(2)
    ]);

    const filename = `profit_report_${startDate || 'all'}_${endDate || 'dates'}.csv`;
    exportToCSV(filename, headers, rows);
    toast.success(t('exportSuccess') || 'File exported successfully');
  };

  return (
    <div>
      <Navbar />
      <div className="reports-container" dir={i18n.dir()}>
        <div className="reports-header">
          <h1>{t('profitsAndReports')}</h1>
        </div>

        <div className="reports-content">

        {/* Date Filter */}
        <div className="date-filter">
          <div className="date-input-group">
            <label>{t('startDate')}:</label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="date-input"
            />
          </div>
          
          <div className="date-input-group">
            <label>{t('endDate')}:</label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="date-input"
            />
          </div>

          <div className="date-input-group">
            <label>{t('allPayments') || 'All Payments'}:</label>
            <select
              value={paymentFilter}
              onChange={(e) => setPaymentFilter(e.target.value)}
              className="date-input"
            >
              <option value="all">{t('allPayments') || 'All Payments'}</option>
              <option value="cash">{i18n.language === 'ar' ? 'نقدي' : i18n.language === 'fr' ? 'Espèces' : 'Cash'}</option>
              <option value="card">{i18n.language === 'ar' ? 'بطاقة' : i18n.language === 'fr' ? 'Carte' : 'Card'}</option>
              <option value="both">{i18n.language === 'ar' ? 'نقدي + بطاقة' : i18n.language === 'fr' ? 'Espèces + Carte' : 'Cash + Card'}</option>
              <option value="debt">{i18n.language === 'ar' ? 'دين' : i18n.language === 'fr' ? 'Crédit' : 'Debt'}</option>
            </select>
          </div>

          <div className="date-input-group">
            <label>{t('allInvoices') || 'All Invoices'}:</label>
            <select
              value={invoiceFilter}
              onChange={(e) => setInvoiceFilter(e.target.value)}
              className="date-input"
            >
              <option value="all">{t('allInvoices') || 'All Invoices'}</option>
              <option value="wholesale">{getWholesaleSalesLabel()}</option>
              <option value="retail">{getRetailSalesLabel()}</option>
            </select>
          </div>

          <button onClick={handleDateChange} className="filter-btn">
            {t('applyFilter')}
          </button>

          {profitData && (
            <button onClick={handleExportCSV} className="export-btn">
              {t('exportCSV')}
            </button>
          )}
        </div>

        {loading ? (
          <div className="loading">{t('loading')}...</div>
        ) : (
          <>
            {/* Summary Cards */}
            {profitData && (
              <div className="summary-cards">
                <div className="summary-card sales-usd">
                  <h3>{i18n.language === 'ar' ? 'إجمالي قيمة المبيعات USD' : i18n.language === 'fr' ? 'Total Ventes USD' : 'Total Sales USD'}</h3>
                  <div className="summary-value">
                    ${toNumber(profitData.summary?.total_sales_usd || 0).toLocaleString('en-US', {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2
                    })}
                  </div>
                </div>

                <div className="summary-card sales-lbp">
                  <h3>{i18n.language === 'ar' ? 'إجمالي قيمة المبيعات LBP' : i18n.language === 'fr' ? 'Total Ventes LBP' : 'Total Sales LBP'}</h3>
                  <div className="summary-value">
                    {toNumber(profitData.summary?.total_sales_lbp || 0).toLocaleString('en-US', {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2
                    })} {t('lbpSymbol')}
                  </div>
                </div>

                <div className="summary-card debt-usd">
                  <h3>{i18n.language === 'ar' ? 'إجمالي الديون USD' : i18n.language === 'fr' ? 'Total Dettes USD' : 'Total Debt USD'}</h3>
                  <div className="summary-value" style={{ color: '#e74c3c' }}>
                    ${toNumber(profitData.summary?.total_debt_usd || 0).toLocaleString('en-US', {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2
                    })}
                  </div>
                </div>

                <div className="summary-card debt-lbp">
                  <h3>{i18n.language === 'ar' ? 'إجمالي الديون LBP' : i18n.language === 'fr' ? 'Total Dettes LBP' : 'Total Debt LBP'}</h3>
                  <div className="summary-value" style={{ color: '#e74c3c' }}>
                    {toNumber(profitData.summary?.total_debt_lbp || 0).toLocaleString('en-US', {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2
                    })} {t('lbpSymbol')}
                  </div>
                </div>

                <div className="summary-card net-profit-usd" style={{ borderLeft: toNumber(profitData.summary?.net_profit_usd || 0) < 0 ? '4px solid #e74c3c' : '4px solid #27ae60' }}>
                  <h3>{i18n.language === 'ar' ? 'الربح النهائي USD' : i18n.language === 'fr' ? 'Profit Net USD' : 'Net Profit USD'}</h3>
                  <div className="summary-value" style={{ color: toNumber(profitData.summary?.net_profit_usd || 0) < 0 ? '#e74c3c' : '#27ae60', fontWeight: 'bold' }}>
                    {toNumber(profitData.summary?.net_profit_usd || 0) < 0 ? '-' : ''}${Math.abs(toNumber(profitData.summary?.net_profit_usd || 0)).toLocaleString('en-US', {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2
                    })}
                  </div>
                </div>

                <div className="summary-card net-profit-lbp" style={{ borderLeft: toNumber(profitData.summary?.net_profit_lbp || 0) < 0 ? '4px solid #e74c3c' : '4px solid #27ae60' }}>
                  <h3>{i18n.language === 'ar' ? 'الربح النهائي LBP' : i18n.language === 'fr' ? 'Profit Net LBP' : 'Net Profit LBP'}</h3>
                  <div className="summary-value" style={{ color: toNumber(profitData.summary?.net_profit_lbp || 0) < 0 ? '#e74c3c' : '#27ae60', fontWeight: 'bold' }}>
                    {toNumber(profitData.summary?.net_profit_lbp || 0) < 0 ? '-' : ''}
                    {Math.abs(toNumber(profitData.summary?.net_profit_lbp || 0)).toLocaleString('en-US', {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2
                    })} {t('lbpSymbol')}
                  </div>
                </div>

                <div className="summary-card exchange-rate">
                  <h3>{i18n.language === 'ar' ? 'سعر الصرف' : i18n.language === 'fr' ? 'Taux de change' : 'Exchange Rate'}</h3>
                  <div className="summary-value">
                    1 USD = {toNumber(profitData.summary?.exchange_rate || 0).toLocaleString('en-US', {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2
                    })} {t('lbpSymbol')}
                  </div>
                </div>

                <div className="summary-card settlement-usd" style={{ borderLeft: toNumber(profitData.summary?.settlement_net_usd || 0) < 0 ? '4px solid #e74c3c' : '4px solid #27ae60' }}>
                  <h3>{i18n.language === 'ar' ? 'التصفية النهائية (USD)' : i18n.language === 'fr' ? 'Règlement net (USD)' : 'Settlement Net (USD)'}</h3>
                  <div className="summary-value" style={{ color: toNumber(profitData.summary?.settlement_net_usd || 0) < 0 ? '#e74c3c' : '#27ae60', fontWeight: 'bold' }}>
                    {toNumber(profitData.summary?.settlement_net_usd || 0) < 0 ? '-' : ''}${Math.abs(toNumber(profitData.summary?.settlement_net_usd || 0)).toLocaleString('en-US', {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2
                    })}
                  </div>
                  <div style={{ fontSize: '12px', marginTop: '6px', color: '#7f8c8d' }}>
                    {profitData.summary?.settlement_status === 'loss'
                      ? (i18n.language === 'ar' ? 'خسارة بعد التصفية' : i18n.language === 'fr' ? 'Perte après règlement' : 'Loss after settlement')
                      : profitData.summary?.settlement_status === 'profit'
                        ? (i18n.language === 'ar' ? 'ربح بعد التصفية' : i18n.language === 'fr' ? 'Profit après règlement' : 'Profit after settlement')
                        : (i18n.language === 'ar' ? 'تعادل بعد التصفية' : i18n.language === 'fr' ? 'Équilibre après règlement' : 'Break-even after settlement')}
                  </div>
                </div>
              </div>
            )}

            {/* Profit by Category Chart - Separated by Currency */}
            {profitData && (profitByCategoryAndCurrency.USD.length > 0 || profitByCategoryAndCurrency.LBP.length > 0) && (
              <>
                {profitByCategoryAndCurrency.USD.length > 0 && (
                  <div className="charts-grid">
                    <div className="chart-card">
                      <h3>{t('profitByCategory')} - USD</h3>
                      <ResponsiveContainer width="100%" height={300}>
                        <BarChart data={profitByCategoryAndCurrency.USD}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="name" />
                          <YAxis />
                          <Tooltip />
                          <Legend />
                          <Bar dataKey="profit" fill="#27ae60" name={`${t('profit')} ($)`} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>

                    <div className="chart-card">
                      <h3>{t('salesByCategory')} - USD</h3>
                      <ResponsiveContainer width="100%" height={300}>
                        <PieChart>
                          <Pie
                            data={profitByCategoryAndCurrency.USD}
                            cx="50%"
                            cy="50%"
                            labelLine={false}
                            label={({ name, count }) => `${name}: ${count}`}
                            outerRadius={80}
                            fill="#8884d8"
                            dataKey="count"
                          >
                            {profitByCategoryAndCurrency.USD.map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                            ))}
                          </Pie>
                          <Tooltip />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                )}

                {profitByCategoryAndCurrency.LBP.length > 0 && (
                  <div className="charts-grid">
                    <div className="chart-card">
                      <h3>{t('profitByCategory')} - LBP</h3>
                      <ResponsiveContainer width="100%" height={300}>
                        <BarChart data={profitByCategoryAndCurrency.LBP}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="name" />
                          <YAxis />
                          <Tooltip />
                          <Legend />
                          <Bar dataKey="profit" fill="#3498db" name={`${t('profit')} (LL)`} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>

                    <div className="chart-card">
                      <h3>{t('salesByCategory')} - LBP</h3>
                      <ResponsiveContainer width="100%" height={300}>
                        <PieChart>
                          <Pie
                            data={profitByCategoryAndCurrency.LBP}
                            cx="50%"
                            cy="50%"
                            labelLine={false}
                            label={({ name, count }) => `${name}: ${count}`}
                            outerRadius={80}
                            fill="#8884d8"
                            dataKey="count"
                          >
                            {profitByCategoryAndCurrency.LBP.map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                            ))}
                          </Pie>
                          <Tooltip />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                )}
              </>
            )}

            {/* Sales Percentage by Item */}
            {profitData && salesPercentageByItem.length > 0 && (
              <div className="chart-section">
                <h2>{t('salesPercentageByItem') || 'Sales Distribution by Item'}</h2>
                <ResponsiveContainer width="100%" height={Math.max(350, salesPercentageByItem.length * 40)}>
                  <BarChart
                    data={salesPercentageByItem}
                    layout="vertical"
                    margin={salesDistributionChartMargin}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis type="number" />
                    <YAxis dataKey="name" type="category" width={175} />
                    <Tooltip
                      contentStyle={{ whiteSpace: 'nowrap' }}
                      formatter={(value, name) => {
                        if (name === 'percentage') return `${value}%`;
                        return value;
                      }}
                      content={({ active, payload }) => {
                        if (active && payload && payload.length) {
                          const data = payload[0].payload;
                          return (
                            <div style={{ backgroundColor: '#fff', padding: '8px', border: '1px solid #ccc', borderRadius: '4px' }}>
                              <p><strong>{data.name}</strong></p>
                              <p>الكمية: {data.value}</p>
                              <p>النسبة: {data.percentage}%</p>
                              {data.total_value_usd > 0 && <p>القيمة USD: {toNumber(data.total_value_usd).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>}
                              {data.total_value_lbp > 0 && <p>القيمة LBP: {toNumber(data.total_value_lbp).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>}
                            </div>
                          );
                        }
                        return null;
                      }}
                    />
                    <Bar dataKey="value" fill="#2ecc71" name={t('sold')} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}

          {/* Daily Sales Chart */}
          {chartData && chartData.data && Array.isArray(chartData.data) && chartData.data.length > 0 ? (
            <div className="chart-section">
              <h2>{t('dailySalesChart') || 'Daily Sales'}</h2>
              
              <div className="chart-container">
                <h3 style={{ marginTop: 0 }}>{t('dailyRevenue') || 'Daily Revenue'}</h3>
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={chartData.data}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" />
                    <YAxis yAxisId="left" />
                    <YAxis yAxisId="right" orientation="right" />
                    <Tooltip />
                    <Legend />
                    <Line
                      yAxisId="left"
                      type="monotone"
                      dataKey="total_usd"
                      stroke="#27ae60"
                      name={t('totalUSD')}
                      strokeWidth={2}
                      dot={{ r: 4 }}
                    />
                    <Line
                      yAxisId="right"
                      type="monotone"
                      dataKey="total_lbp"
                      stroke="#3498db"
                      name={t('totalLBP')}
                      strokeWidth={2}
                      dot={{ r: 4 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>

              <div className="chart-container">
                <h3 style={{ marginTop: 0 }}>{t('dailySalesCount') || 'Daily Sales Count'}</h3>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={chartData.data}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="count" fill="#e74c3c" name={t('salesCount')} radius={[8, 8, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          ) : (chartData && (chartData.data === undefined || chartData.data === null || (Array.isArray(chartData.data) && chartData.data.length === 0))) ? (
            <div className="chart-section">
              <h2>{t('dailySalesChart') || 'Daily Sales'}</h2>
              <div className="no-data">{t('noDataForPeriod')}</div>
            </div>
          ) : null}

          {/* Profit Table */}
          {profitData && profitData.items && profitData.items.length > 0 && (
            <div className="profit-table-section">
              <h2>{t('profitByItem')}</h2>
              
              <div className="profit-table-wrapper">
                <table className="profit-table">
                  <thead>
                    <tr>
                      <th>{t('item')}</th>
                      <th>{t('category')}</th>
                      <th>{t('totalSold')}</th>
                      <th>{t('profitUSD')}</th>
                      <th>{t('profitLBP')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {profitData.items.map(item => {
                      const categoryName = i18n.language === 'ar' ? (item.category_name_ar || item.category_name || '-') : i18n.language === 'fr' ? (item.category_name_fr || item.category_name || '-') : (item.category_name || '-');
                      return (
                        <tr key={item.item_id}>
                          <td>{getItemName(item, i18n.language)}</td>
                          <td>{categoryName}</td>
                          <td>{toNumber(item.total_sold).toLocaleString('en-US')}</td>
                          <td className="profit-value">
                            ${toNumber(item.profit_usd).toLocaleString('en-US', {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2
                            })}
                          </td>
                          <td className="profit-value">
                            {toNumber(item.profit_lbp).toLocaleString('en-US', {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2
                            })} {t('lbpSymbol')}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr className="total-row">
                      <td colSpan="3"><strong>{t('total')}</strong></td>
                      <td className="profit-value">
                        <strong>
                          ${toNumber(profitData.total_profit_usd).toLocaleString('en-US', {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2
                          })}
                        </strong>
                      </td>
                      <td className="profit-value">
                        <strong>
                          {toNumber(profitData.total_profit_lbp).toLocaleString('en-US', {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2
                          })} {t('lbpSymbol')}
                        </strong>
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          )}

          {profitData && profitData.items && profitData.items.length === 0 && (
            <div className="no-data">{t('noDataForPeriod')}</div>
          )}
          </>
        )}
        </div>
      </div>
    </div>
  );
};

export default Reports;
